// iOS-specific print overlay function
const createIOSPrintOverlay = (pdfUrl: string): void => {
  // Remove any existing overlay
  const existingOverlay = document.getElementById('ios-print-overlay');
  if (existingOverlay) {
    existingOverlay.remove();
  }

  // Create overlay
  const overlay = document.createElement('div');
  overlay.id = 'ios-print-overlay';
  overlay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.9);
    z-index: 10000;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: white;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  `;

  overlay.innerHTML = `
    <div style="text-align: center; padding: 20px; max-width: 90%;">
      <h2 style="margin-bottom: 20px;">Print PDF Document</h2>
      <p style="margin-bottom: 30px; line-height: 1.5;">
        Choose how you want to print this document:
      </p>
      
      <div style="margin-bottom: 20px;">
        <button id="ios-download-btn" style="
          display: block;
          width: 100%;
          max-width: 300px;
          margin: 10px auto;
          padding: 15px 20px;
          background: #007AFF;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          cursor: pointer;
          touch-action: manipulation;
        ">
          📱 Download & Print via Files App
        </button>
        
        <button id="ios-open-btn" style="
          display: block;
          width: 100%;
          max-width: 300px;
          margin: 10px auto;
          padding: 15px 20px;
          background: #34C759;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          cursor: pointer;
          touch-action: manipulation;
        ">
          🌐 Open in Browser (then Share → Print)
        </button>
        
        <button id="ios-close-btn" style="
          display: block;
          width: 100%;
          max-width: 300px;
          margin: 20px auto 10px;
          padding: 12px 20px;
          background: #FF3B30;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          cursor: pointer;
          touch-action: manipulation;
        ">
          Cancel
        </button>
      </div>
      
      <div style="font-size: 12px; color: #ccc; line-height: 1.4;">
        <p><strong>Alternative:</strong> Long-press the download link and select "Open in New Tab"</p>
      </div>
    </div>
  `;

  // Add event listeners
  const downloadBtn = overlay.querySelector(
    '#ios-download-btn',
  ) as HTMLButtonElement;
  const openBtn = overlay.querySelector('#ios-open-btn') as HTMLButtonElement;
  const closeBtn = overlay.querySelector('#ios-close-btn') as HTMLButtonElement;

  downloadBtn.addEventListener('click', () => {
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'document.pdf';
    link.click();
    overlay.remove();
  });

  openBtn.addEventListener('click', () => {
    window.open(pdfUrl, '_blank');
    overlay.remove();
  });

  closeBtn.addEventListener('click', () => {
    overlay.remove();
  });

  // Close on overlay click (but not on content click)
  overlay.addEventListener('click', e => {
    if (e.target === overlay) {
      overlay.remove();
    }
  });

  document.body.appendChild(overlay);
};

export const printPdf = (() => {
  const isFirefox: boolean = /Gecko\/\d/.test(navigator.userAgent);
  const isAndroid: boolean = /Android/.test(navigator.userAgent);
  const isIOS: boolean =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  const firefoxDelay: number = 1000;
  let iframe: HTMLIFrameElement | null = null;

  return function (
    url: string,
    callback?: () => void,
    onServerError?: () => void,
  ): void {
    if (iframe) {
      iframe.parentNode?.removeChild(iframe);
    }

    iframe = document.createElement('iframe');
    iframe.style.cssText =
      'width: 1px; height: 100px; position: fixed; left: 0; top: 0; opacity: 0; border-width: 0; margin: 0; padding: 0';

    const xhr = new XMLHttpRequest();
    try {
      xhr.responseType = 'arraybuffer';
    } catch {
      window.open(url, '_blank');
      return;
    }

    xhr.addEventListener('load', function () {
      if (xhr.status === 200 || xhr.status === 201) {
        const pdfBlob = new Blob([xhr.response as ArrayBuffer], {
          type: 'application/pdf',
        });
        const iframeUrl = URL.createObjectURL(pdfBlob);

        if (iframe) {
          iframe.src = iframeUrl;

          iframe.addEventListener('load', function () {
            const openPrintPreview = (): void => {
              try {
                iframe?.focus();
                iframe?.contentWindow?.print();
              } catch (error) {
                console.error('Print preview failed: ' + error, error);
              } finally {
                URL.revokeObjectURL(iframeUrl);
              }
            };

            if (isFirefox) {
              window.setTimeout(openPrintPreview, firefoxDelay);
            } else if (isIOS) {
              // iOS specific handling - show overlay immediately
              // Don't auto-download or use popups that are often blocked
              createIOSPrintOverlay(iframeUrl);
            } else if (isAndroid) {
              const newWindow = window.open('', '_blank');
              if (newWindow) {
                newWindow.document.write(`
                  <!DOCTYPE html>
                  <html lang="en">
                  <head>
                      <meta charset="UTF-8">
                      <meta name="viewport" content="width=device-width, initial-scale=1.0">
                      <title>Print PDF</title>
                      <style>
                          /* CSS Reset */
                          * {
                              margin: 0;
                              padding: 0;
                              box-sizing: border-box;
                          }
                  
                          html, body {
                              width: 100%;
                              height: 100%;
                              overflow: hidden;
                          }
                  
                          #printButton {
                              position: fixed;
                              bottom: 10px;
                              left: 50%;
                              transform: translateX(-50%);
                              z-index: 9999;
                              padding: 12px 24px;
                              background: #1F51FF;
                              color: #fff;
                              cursor: pointer;
                              border: none;
                              border-radius: 6px;
                              font-size: 16px;
                          }
                  
                          #pdfContainer {
                              width: 100%;
                              height: 100%;
                              display: flex;
                              align-items: center;
                              justify-content: center;
                          }
                  
                          @media print {
                              #printButton {
                                  display: none;
                              }
                  
                              html, body, #pdfContainer {
                                  margin: 0 !important;
                                  padding: 0 !important;
                                  width: 100%;
                                  height: 100%;
                                  overflow: hidden !important;
                              }
                  
                              #pdfContainer canvas {
                                  width: 100% !important;
                                  height: 100% !important;
                                  page-break-inside: avoid;
                              }
                          }
                      </style>
                  </head>
                  <body>
                  <button id="printButton">Print</button>
                  <div id="pdfContainer"></div>
                  
                  <script src="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.11.338/pdf.min.js"></script>
                  <script>
                      document.getElementById('printButton').addEventListener('click', function() {
                          window.print();
                      });
                  
                      // Initialize PDF.js
                      const pdfContainer = document.getElementById('pdfContainer');
                      
                      const loadingTask = pdfjsLib.getDocument("${iframeUrl}");
                      loadingTask.promise.then(function(pdf) {
                          const numPages = pdf.numPages;
                          
                          function renderPage(pageNum) {
                              pdf.getPage(pageNum).then(function(page) {
                                  const scale = 2;
                                  const viewport = page.getViewport({ scale: scale });
                                  const canvas = document.createElement('canvas');
                                  const context = canvas.getContext('2d');
                                  canvas.height = viewport.height;
                                  canvas.width = viewport.width;
                                  
                                  const renderContext = {
                                      canvasContext: context,
                                      viewport: viewport
                                  };
                                  
                                  page.render(renderContext).promise.then(function() {
                                      if (pageNum === 1) {
                                          pdfContainer.innerHTML = '';
                                      }
                                      pdfContainer.appendChild(canvas);
                                      
                                      canvas.style.display = 'block';
                                      canvas.style.margin = '0 auto';
                                      canvas.style.width = '100%';
                                      canvas.style.height = '100%';
                                      
                                      if (pageNum < numPages) {
                                          renderPage(pageNum + 1);
                                      }
                                  }).catch(function(error) {
                                      console.error('Error rendering page:', error);
                                  });
                              });
                          }
                          
                          renderPage(1);
                      }).catch(function(error) {
                          console.error('Error loading PDF:', error);
                          pdfContainer.innerHTML = '<p>Error loading PDF. Please try again.</p>';
                      });
                  </script>
                  </body>
                  </html>
                `);
                newWindow.document.close();
              } else {
                alert('Please allow popups for this website');
              }
            } else {
              openPrintPreview();
            }
          });

          document.body.appendChild(iframe);
        }
      } else if (xhr.status !== 200 && onServerError) {
        onServerError();
      }
    });

    if (callback) {
      xhr.addEventListener('load', function () {
        if (xhr.status === 200 || xhr.status === 201) {
          setTimeout(callback, isFirefox ? firefoxDelay + 100 : 100);
        }
      });
    }

    xhr.open('GET', url, true);
    xhr.send();
  };
})();
